import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import styles from "./Dropdown.module.css";
import { DropdownArrowIcon } from "@assets";
import { useClickOutside } from "@hooks";

export interface DropdownOption {
  id: string;
  name: string;
  value: string;
}

export interface DropdownProps {
  options: DropdownOption[];
  value?: string;
  placeholder?: string;
  label?: string;
  /** Shows required asterisk on label */
  required?: boolean;
  disabled?: boolean;
  onChange: (value: string) => void;
  id?: string;
  className?: string;
  error?: string;
  searchable?: boolean;
  /** Matches registration modal input height and styling */
  size?: "default" | "compact";
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  placeholder = "Select option",
  label,
  required = false,
  disabled = false,
  onChange,
  id,
  className,
  error,
  searchable = false,
  size = "default",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [isKeyboardNavigation, setIsKeyboardNavigation] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useClickOutside<HTMLDivElement>(() => {
    setIsOpen(false);
    setFocusedIndex(-1);
    setHoveredIndex(-1);
    setIsKeyboardNavigation(false);
    setSearchValue("");
  });

  const selectedOption = options.find((option) => option.value === value);

  // Filtering algorithm
  const filteredOptions = useMemo(() => {
    if (!searchable || !searchValue.trim()) {
      return options;
    }

    const searchTerm = searchValue.toLowerCase().trim();

    // First priority: starts with search term
    const startsWithMatches = options.filter((option) =>
      option.name.toLowerCase().startsWith(searchTerm)
    );

    // Second priority: contains search term (but doesn't start with it)
    const containsMatches = options.filter(
      (option) =>
        option.name.toLowerCase().includes(searchTerm) &&
        !option.name.toLowerCase().startsWith(searchTerm)
    );

    return [...startsWithMatches, ...containsMatches];
  }, [options, searchValue, searchable]);

  const displayValue = useMemo(() => {
    if (isOpen && searchable) {
      return searchValue;
    }
    return selectedOption ? selectedOption.name : "";
  }, [isOpen, searchable, searchValue, selectedOption]);

  const highlightMatchingText = (text: string, searchTerm: string) => {
    if (!searchTerm.trim()) return text;

    const regex = new RegExp(
      `(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className={styles.highlight}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const handleOptionSelect = useCallback(
    (option: DropdownOption) => {
      onChange(option.value);
      setIsOpen(false);
      setFocusedIndex(-1);
      setHoveredIndex(-1);
      setIsKeyboardNavigation(false);
      setSearchValue("");
      (searchable ? inputRef.current : buttonRef.current)?.focus();
    },
    [onChange, searchable]
  );

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      const currentOptions = filteredOptions;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setIsKeyboardNavigation(true);
          setHoveredIndex(-1);
          setFocusedIndex((prev) =>
            prev < currentOptions.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          event.preventDefault();
          setIsKeyboardNavigation(true);
          setHoveredIndex(-1);
          setFocusedIndex((prev) =>
            prev > 0 ? prev - 1 : currentOptions.length - 1
          );
          break;
        case "Enter":
          event.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < currentOptions.length) {
            handleOptionSelect(currentOptions[focusedIndex]);
          }
          break;
        case "Escape":
          event.preventDefault();
          setIsOpen(false);
          setFocusedIndex(-1);
          setHoveredIndex(-1);
          setIsKeyboardNavigation(false);
          setSearchValue("");
          (searchable ? inputRef.current : buttonRef.current)?.focus();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, focusedIndex, filteredOptions, handleOptionSelect, searchable]);

  const handleToggle = () => {
    if (disabled) return;

    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);

    if (newIsOpen) {
      setFocusedIndex(-1);
      setHoveredIndex(-1);
      setIsKeyboardNavigation(false);
      if (searchable) {
        setSearchValue("");
        // Focus input after state update
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    } else {
      setSearchValue("");
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setSearchValue(newValue);
    setFocusedIndex(-1);
    setHoveredIndex(-1);
    setIsKeyboardNavigation(false);

    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    if (!disabled && searchable) {
      setIsOpen(true);
    }
  };

  // Option selection handler
  const handleOptionClick = (e: React.MouseEvent, option: DropdownOption) => {
    e.preventDefault();
    e.stopPropagation();
    handleOptionSelect(option);
  };

  // Handle mouse down for better touch/click compatibility
  const handleOptionMouseDown = (e: React.MouseEvent) => {
    // Prevent focus changes that might interfere with selection
    e.preventDefault();
  };

  // Reset focused index when filtered options change
  useEffect(() => {
    if (focusedIndex >= filteredOptions.length) {
      setFocusedIndex(filteredOptions.length > 0 ? 0 : -1);
    }
  }, [filteredOptions.length, focusedIndex]);

  return (
    <div
      ref={dropdownRef}
      className={`${styles.dropdown} ${size === "compact" ? styles.compact : ""} ${className || ""}`}
    >
      {label && (
        <label htmlFor={id} className={`${styles.label} ${required ? styles.labelRequired : ""}`}>
          {label}
        </label>
      )}

      <div className={styles.dropdownWrapper}>
        {searchable ? (
          <div className={styles.inputWrapper}>
            <input
              ref={inputRef}
              id={id}
              type="text"
              className={`${styles.trigger} ${styles.searchInput} ${
                disabled ? styles.disabled : ""
              } ${error ? styles.error : ""} ${
                selectedOption ? styles.populated : ""
              }`}
              placeholder={placeholder}
              value={displayValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              disabled={disabled}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
              autoComplete="off"
            />
            <button
              type="button"
              className={styles.arrowButton}
              onClick={handleToggle}
              disabled={disabled}
              tabIndex={-1}
            >
              <div className={`${styles.arrow} ${isOpen ? styles.open : ""}`}>
                <img
                  src={DropdownArrowIcon}
                  alt="Dropdown arrow"
                  width="18"
                  height="10"
                />
              </div>
            </button>
          </div>
        ) : (
          <button
            ref={buttonRef}
            id={id}
            type="button"
            className={`${styles.trigger} ${disabled ? styles.disabled : ""} ${
              error ? styles.error : ""
            } ${selectedOption ? styles.populated : ""}`}
            onClick={handleToggle}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            <span className={styles.triggerText}>
              {selectedOption ? selectedOption.name : placeholder}
            </span>
            <div className={`${styles.arrow} ${isOpen ? styles.open : ""}`}>
              <img
                src={DropdownArrowIcon}
                alt="Dropdown arrow"
                width="18"
                height="10"
              />
            </div>
          </button>
        )}

        <AnimatePresence>
          {isOpen && !disabled && (
            <motion.div
              className={styles.dropdownList}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15 }}
              role="listbox"
              onMouseMove={() => {
                if (isKeyboardNavigation) {
                  setIsKeyboardNavigation(false);
                  setFocusedIndex(-1);
                }
              }}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <motion.button
                    key={option.id}
                    type="button"
                    className={`${styles.option} ${
                      focusedIndex === index && isKeyboardNavigation
                        ? styles.focused
                        : ""
                    } ${
                      hoveredIndex === index && !isKeyboardNavigation
                        ? styles.hovered
                        : ""
                    } ${option.value === value ? styles.selected : ""}`}
                    onClick={(e) => handleOptionClick(e, option)}
                    onMouseDown={(e) => handleOptionMouseDown(e)}
                    onMouseEnter={() => {
                      setIsKeyboardNavigation(false);
                      setFocusedIndex(-1);
                      setHoveredIndex(index);
                    }}
                    onMouseLeave={() => {
                      setHoveredIndex(-1);
                    }}
                    role="option"
                    aria-selected={option.value === value}
                    transition={{ duration: 0.1 }}
                  >
                    {searchable && searchValue.trim()
                      ? highlightMatchingText(option.name, searchValue)
                      : option.name}
                  </motion.button>
                ))
              ) : (
                <div className={styles.noResults}>No options found</div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {error && (
        <motion.p
          className={styles.errorMessage}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {error}
        </motion.p>
      )}
    </div>
  );
};

export default Dropdown;
